import { useI18n } from '@react/hooks';
import type { TaskActivity } from '@core/types/task';
import { ActorReference } from '@/features/references/ActorReference';
import { TaskReference } from '@/features/references/TaskReference';
import { ParentLink } from '@/features/task/detail/ParentLink';
import { UserName } from '@/features/userprofile/UserName';
import { formatActivityType, taskPriorityMap, taskStatusMap } from './logic';

const typeKeys: Record<string, string> = {
  TASK_ACTIVITY_TYPE_CREATED: 'created',
  TASK_ACTIVITY_TYPE_UPDATED: 'updated',
  TASK_ACTIVITY_TYPE_STATUS_CHANGED: 'statusChanged',
  TASK_ACTIVITY_TYPE_ASSIGNED: 'assigned',
  TASK_ACTIVITY_TYPE_UNASSIGNED: 'unassigned',
  TASK_ACTIVITY_TYPE_ATTACHMENT_ADDED: 'attachmentAdded',
  TASK_ACTIVITY_TYPE_ATTACHMENT_REMOVED: 'attachmentRemoved',
  TASK_ACTIVITY_TYPE_DUE_DATE_CHANGED: 'dueDateChanged',
  TASK_ACTIVITY_TYPE_PRIORITY_CHANGED: 'priorityChanged',
  TASK_ACTIVITY_TYPE_PARENT_CHANGED: 'parentChanged',
  TASK_ACTIVITY_TYPE_TIME_LOGGED: 'timeLogged',
  TASK_ACTIVITY_TYPE_ARCHIVED: 'archived',
  TASK_ACTIVITY_TYPE_RESTORED: 'restored',
  TASK_ACTIVITY_TYPE_CONVERSATION_CREATED: 'conversationCreated',
  TASK_ACTIVITY_TYPE_RELATED_ADDED: 'relatedAdded',
  TASK_ACTIVITY_TYPE_RELATED_REMOVED: 'relatedRemoved',
  TASK_ACTIVITY_TYPE_SCHEDULED: 'scheduled',
  TASK_ACTIVITY_TYPE_RESCHEDULED: 'rescheduled',
  TASK_ACTIVITY_TYPE_SCHEDULE_CANCELLED: 'scheduleCancelled',
  TASK_ACTIVITY_TYPE_CUSTOM_FIELD_CHANGED: 'customFieldChanged',
  TASK_ACTIVITY_TYPE_CUSTOM_FIELD_REMOVED: 'customFieldRemoved',
  TASK_ACTIVITY_TYPE_APPROVAL_REQUESTED: 'approvalRequested',
  TASK_ACTIVITY_TYPE_APPROVAL_GRANTED: 'approvalGranted',
  TASK_ACTIVITY_TYPE_APPROVAL_REJECTED: 'approvalRejected',
};

const fieldKeys: Record<string, string> = {
  status: 'status',
  assignee: 'assignee',
  priority: 'priority',
  due_date: 'dueDate',
  title: 'title',
  description: 'description',
  parent: 'parent',
};

export function TaskActivityActor({ actor }: { actor?: TaskActivity['actor'] }) {
  const { t } = useI18n();
  const id = actor?.entity?.id;
  if (!id) return <>{t('task.activityFeed.system')}</>;
  return <ActorReference actor={actor} fallbackLabel={t('task.activityFeed.unknownUser')} />;
}

export function TaskActivityTypeLabel({ type, field }: { type?: TaskActivity['activity_type']; field?: string }) {
  const { t } = useI18n();
  if (field === 'subtask') return <>{t(type === 'TASK_ACTIVITY_TYPE_RELATED_REMOVED' ? 'references.subtaskRemoved' : 'references.subtaskAdded')}</>;
  if (!type) return <>{t('task.update')}</>;
  const key = typeKeys[String(type)];
  return <>{key ? t(`task.activityFeed.types.${key}`) : formatActivityType(String(type))}</>;
}

export function TaskActivityChange({ activity, compact = false }: { activity: TaskActivity; compact?: boolean }) {
  const { t } = useI18n();
  const field = activity.field;
  if (!field || (!activity.old_value && !activity.new_value)) return null;
  const fieldKey = fieldKeys[field];
  const label = field === 'subtask' ? t('references.subtask') : field === 'dependency' ? t('references.dependency') : fieldKey ? t(`task.field.${fieldKey}`) : field.replace(/_/g, ' ');
  const value = (raw: string, parentKind?: string) => {
    if (field === 'dependency' || field === 'subtask' || (field === 'parent' && parentKind === 'task')) return <TaskReference id={raw} />;
    if (field === 'parent' && parentKind) return <ParentLink parent={{ id: raw, type_name: parentKind }} />;
    if (field === 'assignee') {
      return <UserName id={raw} showIdFallback={false} fallbackLabel={t('task.activityFeed.unknownUser')} />;
    }
    const mapping = field === 'status' ? taskStatusMap[raw] : field === 'priority' ? taskPriorityMap[raw] : undefined;
    if (mapping) return t(mapping.labelKey);
    return raw;
  };
  return (
    <p className={`${compact ? 'text-xs' : 'text-sm'} text-muted-foreground mt-1 break-words`}>
      <span className="font-medium">{label}</span>
      {activity.old_value && <>{activity.new_value ? ` ${t('task.from')} ` : ': '}{value(activity.old_value, activity.metadata?.old_parent_type)}</>}
      {activity.new_value && <>{activity.old_value ? ` ${t('task.to')} ` : ': '}{value(activity.new_value, activity.metadata?.new_parent_type)}</>}
      {activity.old_value && !activity.new_value && field === 'assignee' && <> → {t('task.unassigned')}</>}
    </p>
  );
}

export function TaskActivityChangedFields({ fields }: { fields: string }) {
  const { t } = useI18n();
  const labels = fields.split(',').map((path) => {
    const field = path.trim().replace(/^task\./, '');
    if (field === 'all') return t('task.activityFeed.allFields');
    if (field === 'metadata') return t('task.activityFeed.metadata');
    const key = fieldKeys[field];
    return key ? t(`task.field.${key}`) : field.replace(/_/g, ' ');
  });
  return <>{labels.join(', ')}</>;
}
